import { yupResolver } from "@hookform/resolvers/yup";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Divider,
  Stack,
  Typography,
} from "@mui/material";
import {
  Button,
  DownArrowIcon,
  Input,
  SelectField,
} from "components";
import {
  useGetAllCountries,
  useGetAllStateByCountryId,
  useGlobalStore,
  useUpdateCustomerDetails,
} from "controller";
import { useGetDoesHaveCustomerFullAccess } from "pages/modules/customer/context";
import { useEffect, useMemo } from "react";
import { useForm } from "react-hook-form";
import toast from "react-hot-toast";
import { useParams } from "react-router-dom";
import { IndustryList } from "shared";
import theme from "utilz/theme/theme";
import * as yup from "yup";

const schema = yup.object({
  customer_code: yup
    .string()
    .min(3, "Customer code must be at least 3 characters")
    .max(10, "Customer code must be at most 10 characters")
    .required("Customer code is required"),
  customer_type: yup.string(),
  sector: yup.string(),
  address_1: yup.string().trim().required("Address is required"),
  address_2: yup.string(),
  country: yup.string().required("Country is required"),
  state: yup.string().nullable().optional(),
  city: yup
    .string()
    .trim()
    .nullable()
    .optional()
    .max(50, "City must be at most 50 characters")
    .matches(/\p{L}/u, {
      message: "City must contain at least one letter",
      excludeEmptyString: true,
    }),
  zip_code: yup
    .string()
    .max(25, "Zip code must be at most 25 characters")
    .nullable()
    .optional(),
  street: yup
    .string()
    .max(100, "Street must be at most 100 characters")
    .nullable()
    .optional(),
});

interface customerDetailsType {
  customer_code: string;
  customer_type: string;
  sector: string;
  address_1: string;
  address_2: string;
  country: number;
  state: number;
  city: string;
  zip_code: string;
  street: string;
  country_id: number;
  state_id: number;
}

const CustomerDetailsComponent = ({
  customerDetails,
}: {
  customerDetails: customerDetailsType;
}) => {
  const {
    mutateAsync: updateCustomerDetails,
    isLoading: isUpdateCustomerDetailsLoading,
  } = useUpdateCustomerDetails();

  const { id: customerId }: any = useParams();
  const defaultCustomerId = useGlobalStore((state) => state.defaultCustomer);
  const isDefaultCustomer = defaultCustomerId === customerId;

  const haveFullAccess = useGetDoesHaveCustomerFullAccess();
  const inComingValues = useMemo(
    () => ({
      customer_code: customerDetails?.customer_code || "",
      customer_type: customerDetails?.customer_type || "",
      address_1: customerDetails?.address_1 || "",
      address_2: customerDetails?.address_2 || "",
      sector: customerDetails?.sector || "",
      country: customerDetails?.country_id || "",
      state: customerDetails?.state_id || "",
      city: customerDetails?.city || "",
      zip_code: customerDetails?.zip_code || "",
      street: customerDetails?.street || "",
      // zip_code removed - not auto-updated from customerDetails
    }),
    [customerDetails],
  );

  function getSectoreList() {
    return IndustryList;
  }

  // getSectoreList();

  const {
    handleSubmit,
    control,
    formState: { errors, isDirty },
    reset,
    watch,
  } = useForm({
    defaultValues: {
      customer_code: customerDetails?.customer_code || "",
      customer_type: customerDetails?.customer_type || "",
      address_1: customerDetails?.address_1 || "",
      address_2: customerDetails?.address_2 || "",
      sector: customerDetails?.sector || "",
      country: customerDetails?.country_id || "",
      state: customerDetails?.state_id || "",
      city: customerDetails?.city || "",
      zip_code: customerDetails?.zip_code || "",
      // zip_code: "", // Not auto-populated from customerDetails
      street: customerDetails?.street || "",
    },
    resolver: yupResolver(schema),
  });
  const watchCountry: any = watch("country");

  useEffect(() => {
    if (inComingValues) {
      reset(inComingValues);
    }
  }, [inComingValues]);

  const { data: countries } = useGetAllCountries();
  const { data: provinces } = useGetAllStateByCountryId(watchCountry);
  // Get the customer type data
  const customerTypeList = () => {
    return [
      { value: "business", label: "Business" },
      { value: "individual", label: "Individual" },
    ];
  };

  const handleSave = handleSubmit(async (data: any) => {
    try {
      const response = await updateCustomerDetails({
        customerId,
        bodyData: data,
      });
      reset(data);
      toast.success("Customer details updated successfully");
    } catch (error: any) {
      reset();
      if (typeof error === "string") {
        toast.error(error);
      } else {
        toast.error(
          error?.response?.data?.message || "Failed to update customer details",
        );
      }
    }
  });

  return (
    <Accordion
      disableGutters
      defaultExpanded={true}
      elevation={0}
      sx={{
        borderBottom: `1px solid ${theme.palette.neutral["010"]}`,
      }}
    >
      <AccordionSummary
        expandIcon={<DownArrowIcon fontSize="small" />}
        sx={{
          height: "48px",
          padding: "35px 16px 35px 16px",
        }}
      >
        <Box
          sx={{
            width: "100%",
            display: "flex",
            flexDirection: "column",
            gap: "6px",
          }}
        >
          <Typography variant="h6">Customer Details</Typography>
          <Typography variant="body-small" color={theme.palette.lightBg.low}>
            Update customer details here
          </Typography>
        </Box>
      </AccordionSummary>
      <AccordionDetails sx={{ padding: "0px" }}>
        <Stack
          padding={"15px 16px 5px 16px"}
          gap="32px"
          divider={<Divider flexItem orientation="horizontal" />}
        >
          <Stack gap={"20px"}>
            <Input
              name={"customer_code"}
              control={control}
              label="Customer Code*"
              disabled={!haveFullAccess || isDefaultCustomer}
              error={!!errors?.customer_code}
              errorMessage={errors?.customer_code?.message}
              sx={{ width: "100%" }}
              InputProps={{ readOnly: false }}
            />
            <SelectField
              control={control}
              name="customer_type"
              label="Customer Type"
              options={customerTypeList()}
              labelMappingKey="label"
              valueMappingKey="value"
              disabled={!haveFullAccess || isDefaultCustomer}
              error={!!errors?.customer_type}
              errorMessage={errors?.customer_type?.message}
            />
            <SelectField
              control={control}
              name="sector"
              label="Industry"
              options={getSectoreList()}
              labelMappingKey="label"
              valueMappingKey="value"
              disabled={!haveFullAccess || isDefaultCustomer}
              error={!!errors?.sector}
              errorMessage={errors?.sector?.message}
            />
          </Stack>
          {/* <Divider sx={{ margin: "12px 0 30px 0" }} /> */}
          <Stack gap={"20px"}>
            <Input
              name={"address_1"}
              control={control}
              disabled={!haveFullAccess || isDefaultCustomer}
              isResponsive={false}
              required
              label="address 1"
              sx={{ width: "100%" }}
              // @ts-ignore
              errorMessage={errors?.address_1?.message || ""}
              error={errors?.address_1 ? true : false}
            />
            <Input
              name={"address_2"}
              label="address 2"
              control={control}
              sx={{ width: "100%" }}
              disabled={!haveFullAccess || isDefaultCustomer}
              // @ts-ignore
              errorMessage={errors?.address_2?.message || ""}
              error={errors?.address_2 ? true : false}
            />
            <SelectField
              name={"country"}
              label="Country"
              isSearchable
              keepMenuItemOriginalCasing
              control={control}
              options={countries}
              required={true}
              disabled={!haveFullAccess || isDefaultCustomer}
              labelMappingKey="name"
              valueMappingKey="id"
              isResponsive={false}
              sx={{ width: "100%" }}
              size={"small"}
              error={!!errors?.country}
              errorMessage={errors?.country?.message}
            />
            <SelectField
              disabled={!haveFullAccess || !watchCountry || isDefaultCustomer}
              name={"state"}
              label="State"
              isSearchable
              keepMenuItemOriginalCasing
              control={control}
              options={provinces}
              labelMappingKey="state_name"
              valueMappingKey="id"
              isResponsive={false}
              sx={{ width: "100%" }}
              size="small"
              error={!!errors?.state}
              errorMessage={errors?.state?.message}
            />
            <Input
              disabled={!haveFullAccess || isDefaultCustomer}
              name={"city"}
              label="City"
              control={control}
              isResponsive={false}
              sx={{ width: "100%" }}
              size="small"
              error={!!errors?.city}
              errorMessage={errors?.city?.message}
              inputProps={{ maxLength: 50 }}
            />
            {/* <Input
              control={control}
              name={"street"}
              label="Street"
              disabled={!haveFullAccess || isDefaultCustomer}
              sx={{ width: "100%" }}
              error={!!errors?.street}
            /> */}
            <Input
              control={control}
              name={"zip_code"}
              label="ZIP/Postal Code"
              disabled={!haveFullAccess || isDefaultCustomer}
              sx={{ width: "100%" }}
              error={!!errors?.zip_code}
              errorMessage={errors?.zip_code?.message}
              inputProps={{ maxLength: 25 }}
            />
          </Stack>
          {haveFullAccess && (
            <Box
              sx={{
                display: "flex",
                gap: "12px",
                alignItems: "center",
                justifyContent: "flex-end",
              }}
            >
              <Button
                variant="secondary"
                onClick={() => {
                  reset();
                }}
                disabled={!isDirty || isDefaultCustomer}
              >
                Cancel
              </Button>
              <Button
                disabled={!isDirty || isDefaultCustomer}
                loading={isUpdateCustomerDetailsLoading}
                onClick={() => handleSave()}
              >
                Save
              </Button>
            </Box>
          )}
        </Stack>
        {/* <Divider /> */}
      </AccordionDetails>
    </Accordion>
  );
};
export { CustomerDetailsComponent };
